import { MY_ESTIMATE_REQUEST_STATUS_LABELS, type MyEstimateGroup } from '@massilia/queries'
import { StatusBadge } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ChevronRight } from 'lucide-react'

import {
  REQUEST_BADGE_ICONS,
  REQUEST_BADGE_TONES,
  summarizeRequest,
} from './estimate-request-summary'

interface EstimateRequestRowProps {
  group: MyEstimateGroup
  onOpen: () => void
}

const formatDay = (iso: string) => format(new Date(iso), 'd MMM yyyy', { locale: fr })

/**
 * Une demande, réduite à ce qui permet de la reconnaître et de décider si on
 * l'ouvre. Le détail des propositions vit dans le panneau
 * (`EstimateRequestDrawer`) : tout déplier d'un coup donnait une page de
 * plusieurs écrans où le client devait défiler pour comparer deux prix.
 *
 * C'est un `<button>` pleine largeur, pas une carte contenant un lien : la
 * cible tactile doit être la ligne entière.
 */
export function EstimateRequestRow({ group, onOpen }: EstimateRequestRowProps) {
  const badge = REQUEST_BADGE_TONES[group.status]
  const BadgeIcon = REQUEST_BADGE_ICONS[group.status]
  const { actionableCount, summary } = summarizeRequest(group)

  return (
    <button
      aria-haspopup="dialog"
      className="flex w-full items-center gap-3 rounded-2xl border border-gray-light-200 p-4 text-left transition-colors hover:border-brand-300 hover:bg-gray-light-50 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:outline-none md:gap-4 md:p-5"
      id={group.id}
      onClick={onOpen}
      type="button"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-base font-semibold break-words text-gray-light-900">{group.route}</h3>
          <StatusBadge
            appearance={badge.appearance}
            icon={BadgeIcon ? <BadgeIcon aria-hidden /> : undefined}
            tone={badge.tone}
          >
            {MY_ESTIMATE_REQUEST_STATUS_LABELS[group.status]}
          </StatusBadge>
        </div>

        <p className="text-xs text-gray-light-600">
          {group.type === 'FLIGHT' ? 'Vol' : 'Séjour'} · {group.passengerCount} voyageur
          {group.passengerCount > 1 ? 's' : ''}
          {group.travelDates.departure && (
            <> · départ le {formatDay(group.travelDates.departure)}</>
          )}
        </p>

        <p className="flex flex-wrap items-center gap-2 text-sm text-gray-light-700">
          {summary}
          {actionableCount > 0 && (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
              {actionableCount} en attente de votre réponse
            </span>
          )}
        </p>
      </div>

      <ChevronRight aria-hidden className="text-gray-light-400 size-5 shrink-0" />
    </button>
  )
}
